/**
 * Shared hook to load ALL debris load tickets for the active incident.
 * Bypasses the 100-record store pagination by querying the API directly.
 * Results are cached per incident ID to avoid redundant fetches across pages.
 */

import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';

export interface TicketData {
  id: number;
  ticketNumber: string;
  contractorId: string;
  zoneId: string;
  debrisType: string;
  measurementMode: string;
  calculatedCubicYards: string;
  unitCount: string;
  weight: string;
  loadPercentage: string;
  status: string;
  disposalSiteId: string;
  truckId: string;
  clinCode: string;
  clinDescription: string;
  extendedCost: string;
  transactionRate: string;
  subcontractorName: string;
  truckNumber: string;
  loadDate: string;
  disposalSiteName: string;
  pickupLat: string;
  pickupLng: string;
  pickupAddress: string;
  pickupTime: string;
  roadName: string;
  roadOwnerName: string;
  roadOwner: string;
  roadClass: string;
  distanceMiles: string;
  [key: string]: unknown;
}

// TMETA still used for fields without real columns (loadPercentage, crewId, etc.)
const TMETA_RE = /\|\|\|TMETA\|\|\|([\s\S]*?)\|\|\|ENDTMETA\|\|\|/;

// Module-level cache so multiple components share the same data
let _cache: { incidentId: string; tickets: TicketData[] } | null = null;
let _loading = false;
let _listeners: Set<() => void> = new Set();

export function invalidateTicketCache() {
  _cache = null;
  notifyListeners();
}

function notifyListeners() {
  _listeners.forEach(fn => fn());
}


export function useAllTickets() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';
  const [tickets, setTickets] = useState<TicketData[]>(_cache?.incidentId === incidentId ? _cache.tickets : []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!incidentId) { setTickets([]); return; }

    // Return cached if same incident
    if (_cache?.incidentId === incidentId && _cache.tickets.length > 0) {
      setTickets(_cache.tickets);
      return;
    }

    // If already loading, just subscribe for updates
    if (_loading) {
      const listener = () => {
        if (_cache?.incidentId === incidentId) setTickets(_cache.tickets);
        setLoading(false);
      };
      _listeners.add(listener);
      setLoading(true);
      return () => { _listeners.delete(listener); };
    }

    let cancelled = false;
    _loading = true;
    setLoading(true);

    (async () => {
      try {
        const { queryRecords } = await import('../api/client');
        const { TABLES } = await import('../api/tables');
        const { FIELDS } = await import('../api/fields');
        const FT = FIELDS.debrisLoadTickets;
        const PAGE = 500;
        let offset = 0, total = Infinity;
        const all: TicketData[] = [];

        while (offset < total && !cancelled) {
          const res = await queryRecords<Record<string, unknown>>(TABLES.debrisLoadTickets, {
            limit: PAGE, offset,
            filters: [{ field: FT.incidentId, operator: 'eq' as const, value: incidentId }],
          });
          total = res.total;
          res.records.forEach((r: any) => {
            // Client-side guard — GAB server filter is unreliable
            if (incidentId && String(r[FT.incidentId] || '') !== incidentId) return;
            // Decode TMETA for fallback on pre-migration records
            const notes = String(r[FT.notes] || '');
            const mm = notes.match(TMETA_RE);
            let meta: any = {};
            if (mm) try { meta = JSON.parse(mm[1]); } catch {}

            all.push({
              id: r.record_id,
              ticketNumber:     String(r[FT.ticketNumber] || ''),
              contractorId:     String(r[FT.contractorId] || ''),
              contractorName:   String(r[FT.contractorName] || ''),
              zoneId:           String(r[FT.zoneId] || ''),
              debrisType:       String(r[FT.debrisType] || ''),
              measurementMode:  String(r[FT.measurementMode] || '') || 'volumetric',
              calculatedCubicYards: String(r[FT.calculatedCubicYards] || '0'),
              unitCount:        String(r[FT.unitCount] || ''),
              weight:           String(r[FT.weight] || ''),
              loadPercentage:   meta.loadPercentage || '',  // no real column
              status:           String(r[FT.status] || ''),
              disposalSiteId:   String(r[FT.disposalSiteId] || ''),
              truckId:          String(r[FT.truckId] || ''),
              distanceMiles:    String(r[FT.distanceMiles] || ''),
              clinCode:         String(r[FT.clinCode] || ''),
              clinDescription:  String(r[FT.clinDescription] || ''),
              transactionRate:  String(r[FT.transactionRate] || ''),
              extendedCost:     String(r[FT.extendedCost] || ''),
              subcontractorName:String(r[FT.subcontractorName] || ''),
              loadingMonitorName: String(r[FT.loadingMonitorName] || ''),
              truckNumber:      String(r[FT.truckNumber] || ''),
              loadDate:         String(r[FT.loadDate] || ''),
              disposalSiteName: String(r[FT.disposalSiteName] || ''),
              pickupLat:        String(r[FT.pickupLat] || ''),
              pickupLng:        String(r[FT.pickupLng] || ''),
              pickupAddress:    String(r[FT.pickupAddress] || ''),
              pickupTime:       String(r[FT.pickupTime] || ''),
              roadName:         String(r[FT.roadName] || ''),
              roadOwnerName:    String(r[FT.roadOwnerName] || ''),
              roadOwner:        String(r[FT.roadOwner] || ''),
              roadClass:        String(r[FT.roadClass] || ''),
            });
          });
          offset += PAGE;
        }

        if (!cancelled) {
          _cache = { incidentId, tickets: all };
          setTickets(all);
          notifyListeners();
        }
      } catch (e) {
        console.error('[useAllTickets] Failed:', e);
      }
      _loading = false;
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [incidentId]);

  return { tickets, loading };
}
